import { useEffect, useState } from "react";
import {
  getRetainedAmounts,
  saveRetainedAmounts,
} from "../api/retainedAmounts";
import { RetainedAmountRow } from "../types/RetainedAmount";

export function useRetainedAmounts(
  documentId: string,
  companyId: string
) {
  const [rows, setRows] = useState<RetainedAmountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      try {
        const data = await getRetainedAmounts(companyId, documentId);

        if (!active) return;

        setRows(
          data.map((r) => ({
            id: crypto.randomUUID(),
            backendId: r.systemId,
            retainedAmount: r.retainedAmount,
            retainingDate: r.retainingDate,
          }))
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [documentId, companyId]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        retainedAmount: 0,
        retainingDate: new Date().toISOString().slice(0, 10),
      },
    ]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const isValid = rows.every(
    (r) => r.retainedAmount > 0 && !!r.retainingDate
  );

  const save = async () => {
    if (!isValid) return;

    setSaving(true);
    try {
      await saveRetainedAmounts(
        companyId,
        documentId,
        rows.map((r) => ({
          retainedAmount: r.retainedAmount,
          retainingDate: r.retainingDate,
        }))
      );
    } finally {
      setSaving(false);
    }
  };

  return {
    rows,
    setRows,
    loading,
    saving,
    isValid,
    addRow,
    removeRow,
    save,
  };
}
